// Calendar mode (`/c/{slug}/calendar`; CALENDAR-C1, docs/design/2026-10-28-calendar.md §3): one
// page, the overlaid calendars in a day, week, month or agenda view. The view, the day in view
// and an event to open (`event`, with `at` for one occurrence of a series: the board's "Events"
// chips link here) live in the search, beside the shell's focus keys.
import { createRoute } from "@tanstack/react-router";
import { CALENDAR_VIEWS, type CalendarViewKind } from "../features/calendar/calendar-model";
import { CalendarFrame, CalendarScreen } from "../features/calendar/calendar-screen";
import { useCompany } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type CalendarSearch = {
  view?: CalendarViewKind;
  date?: string;
  event?: string;
  at?: string;
};

export function validateCalendarSearch(search: Record<string, unknown>): CalendarSearch {
  const out: CalendarSearch = {};
  if (
    typeof search.view === "string" &&
    (CALENDAR_VIEWS as readonly string[]).includes(search.view)
  )
    out.view = search.view as CalendarViewKind;
  if (typeof search.date === "string" && DAY.test(search.date)) out.date = search.date;
  if (typeof search.event === "string" && ID.test(search.event)) out.event = search.event;
  if (typeof search.at === "string" && !Number.isNaN(Date.parse(search.at))) out.at = search.at;
  return out;
}

export const calendarRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "calendar",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...validateCalendarSearch(search),
  }),
  component: function Calendar() {
    const { api } = calendarRoute.useRouteContext();
    const company = useCompany();
    // Full height: the time grid scrolls under the toolbar, not the page.
    return (
      <CalendarFrame>
        <ModeGate modeKey="calendar">
          <CalendarScreen key={company.id} api={api} company={company} />
        </ModeGate>
      </CalendarFrame>
    );
  },
});
